<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}{{ roleName ? `：${roleName}` : '' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleSelectAll">
          全選
        </button>
        <button type="button" class="btn btn-secondary" @click="handleDeselectAll">
          取消全選
        </button>
        <button type="button" class="btn btn-secondary" @click="handleCancel">
          <i class="ri-arrow-left-line"></i> 返回
        </button>
        <button type="button" class="btn btn-primary" @click="handleSave" :disabled="saving">
          <span v-if="saving">儲存中...</span>
          <span v-else>儲存</span>
        </button>
      </div>
    </div>

    <div class="page-container">

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else class="form-container">
      <div class="module-groups">
        <div
          v-for="category in moduleCategories"
          :key="category"
          class="module-group"
        >
          <h3 class="group-title">{{ getCategoryName(category) }}</h3>
          <div class="module-list">
            <label
              v-for="module in getModulesByCategory(category)"
              :key="module.id"
              class="module-item"
            >
              <input
                type="checkbox"
                :value="module.id"
                :checked="selectedModuleIds.includes(module.id)"
                @change="handleModuleToggle(module.id, $event.target.checked)"
              />
              <div class="module-info">
                <span class="module-name">{{ module.moduleName || module.module_name }}</span>
                <span class="badge-group">
                  <span
                    v-for="type in getModuleVisibleAccountTypes(module)"
                    :key="type"
                    :class="MODULE_ACCOUNT_TYPE_BADGE_CLASS[type]"
                  >{{ MODULE_ACCOUNT_TYPE_LABELS[type] }}</span>
                  <span v-if="isModuleOptional(module)" class="badge badge-warning">選購</span>
                </span>
                <span v-if="module.description" class="module-description">
                  {{ module.description }}
                </span>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { rolesService } from '@/api/modules/roles.js';
import { moduleService } from '@/api/modules/modules.js';
import { showSuccess, showError } from '@/utils/swal.js';
import {
  getModuleVisibleAccountTypes,
  isModuleOptional,
  moduleMatchesAccountType,
  filterSelectedModuleIdsForAccountType,
  MODULE_ACCOUNT_TYPE_LABELS,
  MODULE_ACCOUNT_TYPE_BADGE_CLASS
} from '@/utils/moduleVisibility.js';

export default {
  name: 'RoleModules',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { pageTitle: baseTitle } = usePageTitle('設定角色模組');
    const pageTitle = computed(() => baseTitle.value);
    const loading = ref(false);
    const saving = ref(false);
    const roleName = ref('');
    const roleAccountType = ref('');
    const allModules = ref([]);
    const roleModules = ref([]);
    const selectedModuleIds = ref([]);

    const visibleModules = computed(() => {
      return allModules.value.filter(m => moduleMatchesAccountType(m, roleAccountType.value));
    });

    const moduleCategories = computed(() => {
      const keys = new Set(visibleModules.value.map(m => visibilityGroupKey(m)));
      const order = ['system', 'system_platform', 'platform', 'client'];
      return order.filter(key => keys.has(key));
    });

    const getModulesByCategory = (category) => {
      return visibleModules.value.filter(m => visibilityGroupKey(m) === category);
    };

    const visibilityGroupKey = (module) => {
      const types = getModuleVisibleAccountTypes(module);
      if (types.includes('client')) return 'client';
      if (types.includes('system') && types.includes('platform')) return 'system_platform';
      if (types.includes('platform')) return 'platform';
      return 'system';
    };

    const getCategoryName = (category) => {
      const map = {
        system: '僅系統',
        platform: '僅平台',
        system_platform: '系統與平台',
        client: '客戶'
      };
      return map[category] || category;
    };

    const fetchData = async () => {
      loading.value = true;

      try {
        // 獲取角色資訊
        const roleResponse = await rolesService.getRole(route.params.roleCode);
        if (roleResponse && roleResponse.success && roleResponse.data) {
          roleName.value = roleResponse.data.role_name;
          roleAccountType.value = roleResponse.data.account_type || '';
        }

        // 獲取所有啟用的模組
        const modulesResponse = await moduleService.getModules({
          status: 'active',
          all: true
        });
        if (modulesResponse && modulesResponse.success && modulesResponse.data) {
          allModules.value = modulesResponse.data.items || [];
        }

        // 獲取角色已設定的模組
        const roleModulesResponse = await rolesService.getRoleModules(route.params.roleCode);
        if (roleModulesResponse && roleModulesResponse.success && roleModulesResponse.data) {
          roleModules.value = roleModulesResponse.data;
          selectedModuleIds.value = filterSelectedModuleIdsForAccountType(
            allModules.value,
            roleModules.value.map(m => m.id),
            roleAccountType.value
          );
        }
      } catch (err) {
        console.error('載入資料失敗:', err);
        await showError('錯誤', err.response?.data?.message || '載入資料失敗');
        router.push('/system/roles');
      } finally {
        loading.value = false;
      }
    };

    const handleModuleToggle = (moduleId, checked) => {
      if (checked) {
        if (!selectedModuleIds.value.includes(moduleId)) {
          selectedModuleIds.value.push(moduleId);
        }
      } else {
        selectedModuleIds.value = selectedModuleIds.value.filter(id => id !== moduleId);
      }
    };

    const handleSelectAll = () => {
      selectedModuleIds.value = visibleModules.value.map(m => m.id);
    };

    const handleDeselectAll = () => {
      selectedModuleIds.value = [];
    };

    const handleSave = async () => {
      saving.value = true;

      try {
        await rolesService.setRoleModules(
          route.params.roleCode,
          filterSelectedModuleIdsForAccountType(
            allModules.value,
            selectedModuleIds.value,
            roleAccountType.value
          )
        );
        await showSuccess('成功', '角色模組設定成功');
        router.push('/system/roles');
      } catch (err) {
        console.error('儲存失敗:', err);
        await showError('錯誤', err.response?.data?.message || '儲存失敗');
      } finally {
        saving.value = false;
      }
    };

    const handleCancel = () => {
      router.push('/system/roles');
    };

    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      saving,
      roleName,
      allModules,
      moduleCategories,
      selectedModuleIds,
      pageTitle,
      getModulesByCategory,
      getCategoryName,
      getModuleVisibleAccountTypes,
      isModuleOptional,
      MODULE_ACCOUNT_TYPE_LABELS,
      MODULE_ACCOUNT_TYPE_BADGE_CLASS,
      handleModuleToggle,
      handleSelectAll,
      handleDeselectAll,
      handleSave,
      handleCancel
    };
  }
};
</script>

<style scoped>
.page-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.form-container {
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.module-groups {
  margin-bottom: 30px;
}

.module-group {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.module-group:last-child {
  border-bottom: none;
}

.group-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
}

.module-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 10px;
}

.module-item {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.module-item:hover {
  background: #f8f9fa;
  border-color: #007bff;
}

.module-item input[type="checkbox"] {
  margin-right: 10px;
  margin-top: 2px;
  cursor: pointer;
}

.module-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.module-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.module-description {
  font-size: 12px;
  color: #666;
}

.badge-group {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
}
</style>
